import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';
import Button from '../../lib/button/Button';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { IComponentLifecycleProps } from '../../utils/react';
import { CanvasObject, Connection } from '../../lib/canvas';
import {
	EntityType,
	lowercaseFirstLetter,
	UserManagedEntityState,
	READABLE_ENTITY_TYPES,
	getScopeOwner,
	resolveEntityName,
	getDeclaration,
	PASS_THROUGH_CALLABLE_TYPES,
	PassThroughCallableEntityState,
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../../services/editor';
import { CALLED_BY_NODE_ID_SUFFIX } from '../../services/editor/ui';

export function fromSourceAndTargetEntityToConnectionNodeType(
	connection: Connection,
	_sourceEntity: UserManagedEntityState,
	_targetEntity: UserManagedEntityState,
): 'call' | 'value' {
	const connId = connection.id;

	if (
		connId.endsWith('--' + CALLED_BY_NODE_ID_SUFFIX)
		// connId.endsWith('--' + SUCCESS_CALLER_NODE_ID_SUFFIX) ||
		// connId.endsWith('--' + ERROR_CALLER_NODE_ID_SUFFIX) ||
		// connId.endsWith('--' + ENTRY_CALLER_NODE_ID_SUFFIX)
	) {
		return 'call';
	}

	return 'value';
}

export interface IConnectionDeleteButtonProps extends IComponentLifecycleProps {
	id?: string;
	onClick?: (e: any) => void;
	connection: Connection;
	sourceEntity: UserManagedEntityState;
	targetEntity: UserManagedEntityState;
	tooltip: string;
	canvasObject: CanvasObject;
	project: EditorService;
}

export function validateConnectionDeletion(
	connection: Connection,
	sourceEntity: UserManagedEntityState,
	targetEntity: UserManagedEntityState,
	project: EditorService,
): string[] {
	const reasons: string[] = [];

	const connectionType = fromSourceAndTargetEntityToConnectionNodeType(
		connection,
		sourceEntity,
		targetEntity,
	);

	if (connectionType === 'call') {
		if (
			!!targetEntity &&
			targetEntity.type === EntityType.ReturnStatement &&
			targetEntity.calledBy.length === 1
		) {
			reasons.push(
				`A ${lowercaseFirstLetter(
					READABLE_ENTITY_TYPES[EntityType.ReturnStatement].singular,
				)} node must to be triggered, and be part of a chain that originates from an entry-point trigger.`,
			);
		}

		if (
			!!targetEntity &&
			targetEntity.type === EntityType.ContinueStatement &&
			targetEntity.calledBy.length === 1
		) {
			reasons.push(
				`A ${lowercaseFirstLetter(
					READABLE_ENTITY_TYPES[EntityType.ContinueStatement].singular,
				)} node must to be triggered, and be part of a chain that originates from a loop's body.`,
			);
		}

		if (
			!!targetEntity &&
			targetEntity.type === EntityType.BreakStatement &&
			targetEntity.calledBy.length === 1
		) {
			reasons.push(
				`A ${lowercaseFirstLetter(
					READABLE_ENTITY_TYPES[EntityType.BreakStatement].singular,
				)} node must to be triggered, and be part of a chain that originates from a loop's body.`,
			);
		}

		// If deleting the connection would change to incompatible scope
		if (
			!!targetEntity &&
			targetEntity.type === EntityType.VariableInstance &&
			targetEntity.calledBy.length === 1
		) {
			const targetEntScope = getScopeOwner(
				targetEntity,
				project.logic,
			);

			if (targetEntScope?.type !== EntityType.Project) {
				const declarationScopeOwner = getScopeOwner(
					targetEntity.declaration,
					project.logic,
				);

				if (declarationScopeOwner?.type !== EntityType.Project) {
					reasons.push(
						`Deleting this connection would leave the variable instance "${resolveEntityName(targetEntity, project.logic)}" at a global scope, but its declaration isn't global.`,
						`A ${lowercaseFirstLetter(READABLE_ENTITY_TYPES[EntityType.VariableInstance].singular)} needs to be called by another node to stablish its scope.`,
					);
				}
			}
		}

		const targetEntScope = getScopeOwner(
			targetEntity,
			project.logic,
		);

		if (
			!!targetEntity &&
			targetEntity.type === EntityType.InternalCall &&
			targetEntity.calledBy.length === 1
		) {
			if (targetEntScope?.type !== EntityType.Project) {
				const varDecl = getDeclaration(targetEntity.parent);
				const declarationScopeOwner = getScopeOwner(
					varDecl,
					project.logic,
				);

				if (declarationScopeOwner?.type !== EntityType.Project) {
					reasons.push(
						`Deleting this connection would leave this execution at a global level.`,
						`The ${lowercaseFirstLetter(
							READABLE_ENTITY_TYPES[EntityType.InternalCall].singular,
						)} "${resolveEntityName(targetEntity, project.logic)}" needs to be called by another node to stablish its scope.`,
					);
				}
			}
		}

		if (
			PASS_THROUGH_CALLABLE_TYPES.includes(
				targetEntity?.type as EntityType,
			) &&
			(targetEntity as PassThroughCallableEntityState).calledBy
				.length === 1 &&
			((targetEntity as PassThroughCallableEntityState).successCalls
				.length ||
				(targetEntity as PassThroughCallableEntityState).errorCalls
					.length)
		) {
			reasons.push(
				`Deleting this connection would leave the ${lowercaseFirstLetter(
					READABLE_ENTITY_TYPES[targetEntity.type].singular,
				)} "${resolveEntityName(targetEntity, project.logic)}" without a caller, but it has outgoing calls to other nodes in its current scope. Add new callers before deleting this connection.`,
			);
		}
	}

	return reasons;
}

const ConnectionDeleteButton: React.FC<IConnectionDeleteButtonProps> = (
	props,
) => {
	const rootElementRef = useRef(null);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const disabledReasons: string[] = useMemo(() => {
		return validateConnectionDeletion(
			props.connection,
			props.sourceEntity,
			props.targetEntity,
			props.project,
		);
	}, [props.connection, props.sourceEntity, props.targetEntity, props.project]);

	const disabled = !!disabledReasons.length;

	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
				id={`delete-connection-button--popup--body`}
			>
				<div
					style={{
						margin: 0,
						padding: 10,
						maxWidth: '600px',
					}}
				>
					{disabled
						? disabledReasons.map((reason, index) => (
								<>
									<p
										key={index}
										style={{
											margin: 0,
											padding: 0,
											fontSize: '20px',
											fontWeight: 'bold',
										}}
									>
										{reason}
									</p>

									{/* YES, there is two, two works better for spacing */}
									{index < disabledReasons.length - 1 && <br />}
									{index < disabledReasons.length - 1 && <br />}
								</>
							))
						: props.tooltip}
				</div>
			</CanvasPopupBaseComponent>
		),
		[
			props.sourceEntity,
			props.targetEntity,
			props.canvasObject,
			props.tooltip,
			disabledReasons,
		],
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id: 'delete-connection-button--popup',
			anchorQuerySelector: '#delete-connection-button',
			arrowClasses: ['tooltip-arrow'],
			parentCanvasObject: props.canvasObject,
			placement: 'top',
			arrowContainerSelector: '#delete-connection-button--popup--body',
		},
		memoizedCanvasPopupBody,
		// [
		// 	props.sourceEntity,
		// 	props.targetEntity,
		// 	props.canvasObject,
		// 	props.tooltip,
		// 	disabledReasons,
		// ]
	).setupHover({
		closeDelay: 80,
		openDelay: 600,
	});

	return (
		<span ref={rootElementRef}>
			<Button
				ref={canvasPopup.anchorRef}
				onClick={props.onClick}
				type='icon'
				id='delete-connection-button'
				disabled={disabled}
				style={{ maxWidth: '50px', minWidth: 'auto' }}
				size='small'
			>
				<i className='fa-solid fa-trash-can' />
			</Button>
		</span>
	);
};

export default ConnectionDeleteButton;
